import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminGate.css';

const AdminGate = ({ children }) => {
  const [accessKey, setAccessKey] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const navigate = useNavigate();

  // Check if already authenticated
  useEffect(() => {
    const gateAuth = sessionStorage.getItem('adminGateAuth');
    const timestamp = sessionStorage.getItem('adminGateTimestamp');
    
    if (gateAuth === 'true' && timestamp) {
      // Check if session is still valid (24 hours)
      const now = new Date().getTime();
      const sessionTime = parseInt(timestamp);
      const hoursPassed = (now - sessionTime) / (1000 * 60 * 60);
      
      if (hoursPassed < 24) {
        setIsAuthenticated(true);
      } else {
        // Session expired
        sessionStorage.removeItem('adminGateAuth');
        sessionStorage.removeItem('adminGateTimestamp');
      }
    }

    // Check if locked
    const lockUntil = localStorage.getItem('adminGateLock');
    if (lockUntil) {
      const now = new Date().getTime();
      if (now < parseInt(lockUntil)) {
        setIsLocked(true);
        const remainingTime = Math.ceil((parseInt(lockUntil) - now) / 1000 / 60);
        setError(`Too many failed attempts. Locked for ${remainingTime} minutes.`);
      } else {
        localStorage.removeItem('adminGateLock');
        localStorage.removeItem('adminGateAttempts');
      }
    }

    // Load attempt count
    const savedAttempts = localStorage.getItem('adminGateAttempts');
    if (savedAttempts) {
      setAttempts(parseInt(savedAttempts));
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isLocked) {
      return;
    }

    // Get access key from environment variable
    const correctKey = import.meta.env.VITE_ADMIN_ACCESS_KEY || 'VZBL2025SECURE';
    
    if (accessKey === correctKey) {
      // Success - grant access
      sessionStorage.setItem('adminGateAuth', 'true');
      sessionStorage.setItem('adminGateTimestamp', new Date().getTime().toString());
      localStorage.removeItem('adminGateAttempts');
      setIsAuthenticated(true);
      setError('');
    } else {
      // Failed attempt
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      localStorage.setItem('adminGateAttempts', newAttempts.toString());
      
      if (newAttempts >= 5) {
        // Lock for 30 minutes after 5 failed attempts
        const lockUntil = new Date().getTime() + (30 * 60 * 1000);
        localStorage.setItem('adminGateLock', lockUntil.toString());
        setIsLocked(true);
        setError('Too many failed attempts. Access locked for 30 minutes.');
      } else {
        setError(`Invalid access key. ${5 - newAttempts} attempts remaining.`);
      }
      
      setAccessKey('');
    }
  };

  const handleBackToSite = () => {
    navigate('/');
  };

  // If authenticated, render children (admin pages)
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Otherwise, show access gate
  return (
    <div className="admin-gate">
      <div className="admin-gate-content">
        <div className="admin-gate-card">
          <div className="admin-gate-header">
            <div className="lock-icon">🔒</div>
            <h1>Restricted Access</h1>
            <p>This area requires authentication</p>
          </div>

          {!isLocked ? (
            <form onSubmit={handleSubmit} className="admin-gate-form">
              <div className="form-group">
                <label htmlFor="accessKey">Access Key</label>
                <input
                  type="password"
                  id="accessKey"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  placeholder="Enter access key"
                  autoFocus
                  required
                  disabled={isLocked}
                />
              </div>

              {error && (
                <div className="gate-error">
                  {error}
                </div>
              )}

              {attempts > 0 && attempts < 5 && (
                <div className="gate-warning">
                  ⚠️ Warning: {5 - attempts} attempts remaining before lockout
                </div>
              )}

              <button 
                type="submit" 
                className="gate-submit-btn"
                disabled={isLocked}
              >
                Verify Access
              </button>

              <button 
                type="button" 
                onClick={handleBackToSite}
                className="gate-back-btn"
              >
                ← Back to Site
              </button>
            </form>
          ) : (
            <div className="gate-locked">
              <div className="locked-icon">🚫</div>
              <p className="locked-message">{error}</p>
              <p className="locked-info">
                Access has been temporarily suspended due to multiple failed attempts.
                Please try again later or contact the administrator.
              </p>
              <button 
                onClick={handleBackToSite}
                className="gate-back-btn"
              >
                ← Back to Site
              </button>
            </div>
          )}
        </div>

        <div className="admin-gate-footer">
          <p>🔐 Protected by VZBL Security</p>
          <p className="footer-note">Access key required • Session expires after 24 hours</p>
        </div>
      </div>
    </div>
  );
};

export default AdminGate;

